import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Video, Plus, Loader2, Monitor, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ZoomManagement() {
  const [zoomUsers, setZoomUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // {email, name}
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      // V68 — dieticians host their own recorded consultations, so they need a seat too.
      const [zoomRes, instRes, docRes] = await Promise.all([
        api.get('/api/zoom/licensed-users').catch(() => ({ data: { users: [], total_licenses: 0 } })),
        api.get('/api/users', { params: { role: 'instructor', limit: 100 } }),
        api.get('/api/users', { params: { role: 'doctor', limit: 100 } }).catch(() => ({ data: { users: [] } })),
      ]);
      setZoomUsers(zoomRes.data);
      const instructors = (instRes.data.users || []).map((u) => ({ ...u, staff_kind: 'Instructor' }));
      const medics = (docRes.data.users || []).map((u) => ({
        ...u,
        staff_kind: u.medical_category === 'nutritionist' ? 'Dietician' : 'Medical team',
      }));
      setStaff([...instructors, ...medics]);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  // Anyone on staff who doesn't already hold a Zoom seat
  const zoomEmails = (zoomUsers?.users || []).map(u => u.email?.toLowerCase());
  const unassignedStaff = staff.filter(i => !zoomEmails.includes(i.email?.toLowerCase()));
  const unlicensedDieticians = unassignedStaff.filter(i => i.staff_kind === 'Dietician');

  const handleAssignLicense = async () => {
    if (!selectedStaff) return;
    const member = staff.find(i => i.id === selectedStaff);
    if (!member) return;

    setAdding(true);
    try {
      // Add to Zoom licenses in our system
      await api.post(`/api/zoom/licenses?email=${encodeURIComponent(member.email)}&name=${encodeURIComponent(member.name)}`);
      toast.success(`Zoom license assigned to ${member.name}`);
      setAddOpen(false);
      setSelectedStaff('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign license');
    }
    setAdding(false);
  };

  const handleDeleteFromZoom = async () => {
    if (!deleteTarget?.email) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/api/zoom/users/by-email/${encodeURIComponent(deleteTarget.email)}`);
      toast.success(res.data?.message || `Zoom seat released for ${deleteTarget.email}`);
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete from Zoom');
    }
    setDeleting(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Zoom Integration</h1>
          <p className="text-muted-foreground mt-1">Manage Zoom licenses for instructors and dieticians</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button onClick={() => setAddOpen(true)} data-testid="add-zoom-license"><Plus className="h-4 w-4 mr-2" />Add License</Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="rounded-2xl">
          <CardContent className="pt-5 pb-4">
            <Monitor className="h-5 w-5 text-blue-600 mb-3" />
            <p className="text-2xl font-semibold">{zoomUsers?.total_licenses || 0}</p>
            <p className="text-xs text-muted-foreground">Zoom Licensed Users</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl">
          <CardContent className="pt-5 pb-4">
            <Video className="h-5 w-5 text-primary mb-3" />
            <p className="text-2xl font-semibold" data-testid="zoom-unassigned-staff">{unassignedStaff.length}</p>
            <p className="text-xs text-muted-foreground">Staff Without License</p>
            {unlicensedDieticians.length > 0 && (
              <p className="text-[11px] text-amber-700 mt-1" data-testid="zoom-unlicensed-dieticians">
                {unlicensedDieticians.length} dietician(s) have no seat — their consultations cannot be recorded
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="rounded-2xl" data-testid="zoom-licenses-card">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Available Licenses</p>
            <p className="text-2xl font-semibold mt-1" data-testid="zoom-licenses-available">
              {zoomUsers?.plan?.seats_available ?? '—'}
              <span className="text-sm font-normal text-muted-foreground"> free of {zoomUsers?.plan?.total_seats ?? '—'}</span>
            </p>
            <p className="text-xs text-muted-foreground">{zoomUsers?.plan?.seats_in_use != null ? `${zoomUsers.plan.seats_in_use} in use on your Zoom plan` : 'Seat count unavailable from Zoom'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Zoom Account Users */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Zoom Account Users</CardTitle>
          <p className="text-sm text-muted-foreground">Everyone with a Zoom license. Instructors host their classes; dieticians host (and record) their consultations.</p>
        </CardHeader>
        <CardContent>
          {zoomUsers?.users?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zoomUsers.users.map(u => (
                  <TableRow key={u.id} data-testid={`zoom-user-row-${u.email}`}>
                    <TableCell className="font-mono text-sm">{u.email}</TableCell>
                    <TableCell>{u.first_name} {u.last_name}</TableCell>
                    <TableCell><Badge variant="secondary">{u.type === 2 ? 'Licensed' : u.type === 1 ? 'Basic' : 'Other'}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">{u.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteTarget({ email: u.email, name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email })}
                        data-testid={`zoom-delete-${u.email}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Video className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No Zoom licensed users found</p>
              <p className="text-xs text-muted-foreground mt-1">Add licenses to your instructors to enable them to host meetings</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add License Dialog — instructors and dieticians */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Assign Zoom License</DialogTitle>
            <p className="text-sm text-muted-foreground">Pick an instructor or dietician. A dietician needs her own seat so her consultations can be recorded.</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Select team member</label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger data-testid="zoom-license-instructor"><SelectValue placeholder="Choose a team member" /></SelectTrigger>
                <SelectContent position="popper" sideOffset={4} className="z-[200]">
                  {unassignedStaff.length === 0 ? (
                    <SelectItem value="none" disabled>Everyone already has a license</SelectItem>
                  ) : (
                    unassignedStaff.map(i => (
                      <SelectItem key={i.id} value={i.id} data-testid={`zoom-license-option-${i.email}`}>
                        {i.staff_kind} · {i.name} ({i.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            {selectedStaff && (
              <div className="p-3 rounded-xl bg-muted/40">
                <p className="text-xs text-muted-foreground">This creates a Zoom account for them and takes one license from your plan. They can then host — and record — their own meetings.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignLicense} disabled={adding || !selectedStaff}>
              {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Assign License
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete-from-Zoom confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && !deleting && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Remove Zoom seat?</DialogTitle>
            <DialogDescription>
              This will disassociate <strong>{deleteTarget?.email}</strong> from the Zoom tenant and free the Licensed seat.
              Their LMS instructor record (if any) will have its Zoom link cleared — they won't appear here anymore.
              Zoom history and recordings are preserved. Cannot be undone from this UI.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteFromZoom} disabled={deleting} data-testid="zoom-delete-confirm">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Release seat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
